export const PLAN_EDITOR_GROUPS = [
  { id: 'workouts', label: 'Workouts' },
  { id: 'meals', label: 'Meals' },
  { id: 'media', label: 'Media' },
  { id: 'metrics', label: 'Metrics' },
  { id: 'surveys', label: 'Surveys' },
  { id: 'other', label: 'Other' },
];

const categoryForInstruction = instruction => {
  if (['do_workout_generated_manually', 'do_workout_generated_automatically_in_advance', 'do_workout_regenerated_every_time'].includes(instruction)) return 'workouts';
  if (instruction === 'eat_meal') return 'meals';
  if (instruction === 'upload_media') return 'media';
  if (instruction === 'take_measurements') return 'metrics';
  if (instruction === 'take_survey') return 'surveys';
  return 'other';
};

const searchableText = item => [
  item?.name,
  item?.activity_name,
  item?.title,
  item?.autogeneration_hint,
  item?.instruction,
  PLAN_EDITOR_GROUPS.find(group => group.id === categoryForInstruction(item?.instruction))?.label,
].filter(Boolean).join(' ').toLowerCase();

export const WEEK_DAYS = [
  { id: 'mon', full: 'Monday', short: 'Mon', narrow: 'Mo' },
  { id: 'tue', full: 'Tuesday', short: 'Tue', narrow: 'Tu' },
  { id: 'wed', full: 'Wednesday', short: 'Wed', narrow: 'We' },
  { id: 'thu', full: 'Thursday', short: 'Thu', narrow: 'Th' },
  { id: 'fri', full: 'Friday', short: 'Fri', narrow: 'Fr' },
  { id: 'sat', full: 'Saturday', short: 'Sat', narrow: 'Sa' },
  { id: 'sun', full: 'Sunday', short: 'Sun', narrow: 'Su' },
];

const weeklyDayIndex = item => {
  if (item?.recurrence_unit !== 'weeks' || Number(item?.recurrence_number) < 1 || !item?.start_time) return -1;
  const dateText = String(item.start_time).slice(0, 10);
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateText);
  if (!match) return -1;
  const [, year, month, day] = match.map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return -1;
  return (date.getUTCDay() + 6) % 7;
};

export function buildScheduleSections(items = [], minimumWeeklyItems = 2) {
  const canonicalItems = Array.isArray(items) ? items : [];
  const weeklyItems = canonicalItems
    .map((item, index) => ({ item, index, dayIndex: weeklyDayIndex(item) }))
    .filter(entry => entry.dayIndex >= 0);
  if (weeklyItems.length < minimumWeeklyItems) {
    return {
      mode: 'list',
      days: WEEK_DAYS.map(day => ({ ...day, items: [] })),
      occupancy: Array(7).fill(0),
      otherItems: canonicalItems,
    };
  }
  const days = WEEK_DAYS.map((day, dayIndex) => ({
    ...day,
    items: weeklyItems
      .filter(entry => entry.dayIndex === dayIndex)
      .sort((a, b) => a.index - b.index)
      .map(entry => entry.item),
  }));
  const weeklyIds = new Set(weeklyItems.map(entry => String(entry.item?._id)));
  return {
    mode: 'weekly-agenda',
    days,
    occupancy: days.map(day => day.items.length),
    otherItems: canonicalItems.filter(item => !weeklyIds.has(String(item?._id))),
  };
}

export function getSchedulePeers(items = [], targetItem) {
  const schedule = buildScheduleSections(items);
  if (schedule.mode !== 'weekly-agenda') return schedule.otherItems;
  const targetId = String(targetItem?._id);
  const day = schedule.days.find(candidate => candidate.items.some(item => String(item?._id) === targetId));
  return day ? day.items : schedule.otherItems;
}

export function buildPlanEditorView(items = [], { query = '', filter = 'all' } = {}) {
  const canonicalItems = Array.isArray(items) ? items : [];
  const counts = { all: canonicalItems.length, workouts: 0, meals: 0, media: 0, metrics: 0, surveys: 0, other: 0 };
  canonicalItems.forEach(item => { counts[categoryForInstruction(item?.instruction)] += 1; });
  const needle = String(query || '').trim().toLowerCase();
  const visibleItems = canonicalItems.filter(item => {
    const category = categoryForInstruction(item?.instruction);
    return (filter === 'all' || category === filter) && (!needle || searchableText(item).includes(needle));
  });
  const groups = PLAN_EDITOR_GROUPS.map(group => ({
    ...group,
    items: visibleItems.filter(item => categoryForInstruction(item?.instruction) === group.id),
  })).filter(group => group.items.length > 0);
  return { counts, visibleItems, groups };
}

export { categoryForInstruction };
